import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '../component/Header';
import PublicFooter from '../../components/layout/PublicFooter';
import blogPosts from './blogData';
import SEO from '../../components/SEO';

const renderMarkdown = (content) => {
  // Simple markdown-to-JSX renderer for blog content
  const lines = content.trim().split('\n');
  const elements = [];
  let inList = false;
  let listItems = [];
  let inTable = false;
  let tableRows = [];
  let tableHeader = false;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul-${elements.length}`} className="tw-space-y-1.5 tw-my-4 tw-pl-6 tw-list-disc tw-text-gray-700">{listItems}</ul>);
      listItems = [];
      inList = false;
    }
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="tw-overflow-x-auto tw-my-6 tw-rounded-lg tw-border tw-border-gray-200">
          <table className="tw-w-full tw-text-sm">
            <thead><tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">{tableRows[0]}</tr></thead>
            <tbody>{tableRows.slice(1)}</tbody>
          </table>
        </div>
      );
      tableRows = [];
      inTable = false;
      tableHeader = false;
    }
  };

  const inlineFormat = (text) => {
    // Bold
    let parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  };

  const formatWithLinks = (text) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(...inlineFormat(text.slice(lastIndex, match.index)));
      }
      const [, linkText, href] = match;
      if (href.startsWith('/')) {
        parts.push(<Link key={match.index} to={href} className="tw-text-primary-600 hover:tw-underline">{linkText}</Link>);
      } else {
        parts.push(<a key={match.index} href={href} className="tw-text-primary-600 hover:tw-underline" target="_blank" rel="noopener noreferrer">{linkText}</a>);
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(...inlineFormat(text.slice(lastIndex)));
    }
    return parts;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table rows
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      flushList();
      const cells = line.trim().split('|').filter(c => c.trim() !== '');
      if (cells.every(c => /^[\s-:]+$/.test(c))) {
        tableHeader = true;
        continue; // Skip separator row
      }
      if (!inTable) inTable = true;
      const isHeader = tableRows.length === 0 && !tableHeader;
      const row = (
        <tr key={`tr-${i}`} className={tableRows.length > 0 ? 'tw-border-t tw-border-gray-100' : ''}>
          {cells.map((cell, ci) =>
            isHeader
              ? <th key={ci} className="tw-text-left tw-px-4 tw-py-2.5 tw-font-semibold tw-text-gray-700">{cell.trim()}</th>
              : <td key={ci} className="tw-px-4 tw-py-2.5 tw-text-gray-600">{cell.trim()}</td>
          )}
        </tr>
      );
      tableRows.push(row);
      continue;
    } else {
      flushTable();
    }

    // Headers
    if (line.startsWith('## ')) {
      flushList();
      elements.push(<h2 key={i} className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mt-10 tw-mb-4 tw-m-0">{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      flushList();
      elements.push(<h3 key={i} className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mt-8 tw-mb-3 tw-m-0">{line.slice(4)}</h3>);
    } else if (line.startsWith('---')) {
      flushList();
      elements.push(<hr key={i} className="tw-border-t tw-border-gray-200 tw-my-8" />);
    } else if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      inList = true;
      const text = line.trim().slice(2);
      listItems.push(<li key={i} className="tw-text-sm tw-leading-relaxed">{formatWithLinks(text)}</li>);
    } else if (/^\d+\.\s/.test(line.trim())) {
      // Numbered list items rendered as unordered for simplicity
      inList = true;
      const text = line.trim().replace(/^\d+\.\s/, '');
      listItems.push(<li key={i} className="tw-text-sm tw-leading-relaxed">{formatWithLinks(text)}</li>);
    } else if (line.trim() === '') {
      flushList();
    } else if (line.trim().startsWith('*') && line.trim().endsWith('*') && !line.trim().startsWith('**')) {
      flushList();
      const text = line.trim().slice(1, -1);
      elements.push(<p key={i} className="tw-text-sm tw-text-gray-500 tw-italic tw-my-4 tw-m-0">{formatWithLinks(text)}</p>);
    } else {
      flushList();
      elements.push(<p key={i} className="tw-text-base tw-text-gray-700 tw-leading-relaxed tw-my-4 tw-m-0">{formatWithLinks(line.trim())}</p>);
    }
  }
  flushList();
  flushTable();

  return elements;
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const currentIndex = blogPosts.indexOf(post);
  const relatedPosts = blogPosts.filter((_, i) => i !== currentIndex).slice(0, 2);

  const blogPostSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "datePublished": post.date,
    "author": {
      "@type": "Organization",
      "name": "RMT - Renew My Tags",
      "url": "https://renewmytags.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "RMT - Renew My Tags",
      "url": "https://renewmytags.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://renewmytags.com/blog/${post.slug}`
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      <SEO
        title={post.title}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        structuredData={blogPostSchema}
      />
      <Header />

      {/* Header */}
      <div className="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-800 tw-py-14 md:tw-py-20">
        <div className="tw-max-w-3xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
          <Link to="/blog" className="tw-text-primary-200 hover:tw-text-white tw-text-sm tw-no-underline tw-flex tw-items-center tw-gap-1.5 tw-mb-4">
            <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            Back to Blog
          </Link>
          <span className="tw-inline-block tw-text-xs tw-font-semibold tw-text-primary-200 tw-uppercase tw-tracking-wider tw-mb-3">{post.category}</span>
          <h1 className="tw-text-3xl md:tw-text-4xl tw-text-white tw-m-0 tw-leading-tight">{post.title}</h1>
          <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-primary-200 tw-mt-4">
            <span>{post.date}</span>
            <span className="tw-text-primary-300">·</span>
            <span>{post.readTime}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="tw-max-w-3xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-10 md:tw-py-14">
        <article className="tw-prose-custom">
          {renderMarkdown(post.content)}
        </article>

        {/* CTA */}
        <div className="tw-mt-12 tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-700 tw-rounded-2xl tw-p-8 md:tw-p-10 tw-text-center">
          <h3 className="tw-text-2xl tw-font-bold tw-text-white tw-m-0 tw-mb-3">Ready to automate your tag renewal?</h3>
          <p className="tw-text-primary-200 tw-m-0 tw-mb-6">Sign up in under 5 minutes. We'll handle the rest.</p>
          <Link to="/" className="tw-inline-block tw-bg-white tw-text-primary-600 tw-font-semibold tw-px-8 tw-py-3 tw-rounded-lg hover:tw-bg-primary-50 tw-transition-colors tw-no-underline tw-shadow-lg">
            Get Started Free
          </Link>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="tw-mt-14">
            <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-m-0 tw-mb-6">Related Articles</h3>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-5">
              {relatedPosts.map((related) => (
                <Link key={related.slug} to={`/blog/${related.slug}`} className="tw-block tw-group tw-no-underline">
                  <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-5 hover:tw-shadow-md tw-transition-shadow">
                    <span className="tw-text-xs tw-font-semibold tw-text-primary-600 tw-uppercase tw-tracking-wider">{related.category}</span>
                    <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0 tw-mt-2 tw-mb-1 group-hover:tw-text-primary-600 tw-transition-colors tw-leading-snug">{related.title}</h4>
                    <span className="tw-text-xs tw-text-gray-400">{related.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
};

export default BlogPost;
